import {
    Button,
    Chip,
    CircularProgress,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from "@mui/material"
import { useContext, useState } from "react"
import { Cancel as CancelIcon } from "@mui/icons-material"
import styles from "./AddNewResource.module.scss"
import { TrainingContext, TrainingContextPayload } from "../../Context/TrainingPageContext"
import GlobalTextfield from "../GlobalTextfield/GlobalTextfield"
import globalStyles from "../../Styles/ComponentStyles/global.module.scss"

export interface CreateNewResourcePayload {
    resource_title: string
    resource_description: string
    resource_file_id: number
    thumbnail_file_id: number
    resource_coming_soon: boolean
    resource_file_type: "Video" | "Document"
}

const AddNewResource = ({
    onClose,
    uploadResourceAPIFunc,
    onSubmit,
    isEditModeTrainingPage,
    tabValue,
    setTabValue,
}: any) => {
    const { selectedResorceData, setSelectedResorceData } = useContext(
        TrainingContext
    ) as TrainingContextPayload

    const [thumbnailUploading, setThumbnailUploading] = useState(false)
    const [resourceUploading, setResourceUploading] = useState(false)

    const handleResourceTypeChange = (_e: React.ChangeEvent<HTMLInputElement>, value: string) => {
        setTabValue(value as "Video" | "Document")
    }

    const handleInputChange = (field: string, value: any) => {
        setSelectedResorceData((prevState: any) => ({
            ...prevState,
            [field]: value,
        }))
    }

    const handleThumbnailUpload = async (e: any) => {
        setThumbnailUploading(true)
        try {
            const resource_flag: any = "false"
            await uploadResourceAPIFunc(e, resource_flag)
        } finally {
            setThumbnailUploading(false)
        }
    }

    const handleResourceUpload = async (e: any) => {
        setResourceUploading(true)
        try {
            const resource_flag: any = "true"
            await uploadResourceAPIFunc(e, resource_flag)
        } finally {
            setResourceUploading(false)
        }
    }

    const handleRemoveFile = (fileType: "thumbnail" | "resource") => {
        if (fileType === "thumbnail") {
            setSelectedResorceData((prevState: any) => {
                const updatedResourceFileData = Array.isArray(prevState?.resource_file_data)
                    ? prevState.resource_file_data.map((item: any) => (item ? { ...item } : item))
                    : prevState?.resource_file_data

                const updatedState = {
                    ...prevState,
                    thumbnail_file_id: null,
                    thumbnail_file_name: null,
                    resource_file_data: updatedResourceFileData,
                }
                if (isEditModeTrainingPage && updatedState.resource_file_data?.[1]) {
                    updatedState.resource_file_data[1] = {
                        ...updatedState.resource_file_data[1],
                        thumbnail_id: null,
                        thumbnail_name: null,
                    }
                }
                return updatedState
            })
            const thumbnailInput = document.getElementById("thumbnailUpload") as HTMLInputElement
            if (thumbnailInput) {
                thumbnailInput.value = ""
            }
        } else {
            setSelectedResorceData((prevState: any) => {
                const updatedResourceFileData = Array.isArray(prevState?.resource_file_data)
                    ? prevState.resource_file_data.map((item: any) => (item ? { ...item } : item))
                    : prevState?.resource_file_data

                const updatedState = {
                    ...prevState,
                    resource_file_id: null,
                    resource_file_name: null,
                    resource_file_data: updatedResourceFileData,
                }
                if (isEditModeTrainingPage && updatedState.resource_file_data?.[0]) {
                    updatedState.resource_file_data[0] = {
                        ...updatedState.resource_file_data[0],
                        file_id: null,
                        file_name: null,
                    }
                }
                return updatedState
            })
            const resourceInput = document.getElementById(
                tabValue === "Video" ? "videoUpload" : "documentUpload"
            ) as HTMLInputElement
            if (resourceInput) {
                resourceInput.value = ""
            }
        }
    }

    const resourceFileLabel = tabValue === "Video" ? "Video" : "Document"
    const hasThumbnail =
        (selectedResorceData?.thumbnail_file_id && selectedResorceData?.thumbnail_file_name) ||
        (isEditModeTrainingPage &&
            selectedResorceData?.resource_file_data?.[1]?.thumbnail_id &&
            selectedResorceData?.resource_file_data?.[1]?.thumbnail_name)

    const hasResourceFile =
        (selectedResorceData?.resource_file_id && selectedResorceData?.resource_file_name) ||
        (isEditModeTrainingPage &&
            selectedResorceData?.resource_file_data?.[0]?.file_id &&
            selectedResorceData?.resource_file_data?.[0]?.file_name)

    return (
        <Grid item xs={12} className={styles.formGridItem}>
            <Stack spacing={2} className={styles.formRoot}>
                <div className={styles.fieldBlock}>
                    <Typography component="span" className={globalStyles.globalPFTextfieldLabel}>
                        Resource Type
                    </Typography>
                    {!isEditModeTrainingPage ? (
                        <RadioGroup
                            row
                            className={styles.radioRow}
                            value={tabValue}
                            onChange={handleResourceTypeChange}
                        >
                            <FormControlLabel
                                value="Video"
                                control={<Radio className={styles.radioPrimary} color="primary" />}
                                label="Video"
                            />
                            <FormControlLabel
                                value="Document"
                                control={<Radio className={styles.radioPrimary} color="primary" />}
                                label="Document"
                            />
                        </RadioGroup>
                    ) : (
                        <Typography className={styles.resourceTypeReadonly}>
                            {selectedResorceData?.resource_file_type}
                        </Typography>
                    )}
                </div>

                <div className={styles.twoColumnRow}>
                    <div className={styles.fieldBlock}>
                        <Typography component="span" className={globalStyles.globalPFTextfieldLabel}>
                            Title<span className={styles.requiredMark}> *</span>
                        </Typography>
                        <GlobalTextfield
                            required={true}
                            id="resource-title"
                            variant="outlined"
                            placeholder="Enter Title"
                            fullWidth
                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                            value={selectedResorceData.resource_title}
                            onChange={(e) => handleInputChange("resource_title", e?.target?.value)}
                        />
                    </div>
                    <div className={styles.fieldBlock}>
                        <Typography component="span" className={globalStyles.globalPFTextfieldLabel}>
                            Description<span className={styles.requiredMark}> *</span>
                        </Typography>
                        <GlobalTextfield
                            required={true}
                            id="resource-description"
                            variant="outlined"
                            placeholder="Enter Description"
                            fullWidth
                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                            value={selectedResorceData.resource_description}
                            onChange={(e) => handleInputChange("resource_description", e?.target?.value)}
                        />
                    </div>
                </div>

                <div className={styles.twoColumnRow}>
                    <div className={styles.fieldBlock}>
                        <div className={styles.labelWithSpinner}>
                            <Typography component="span" className={globalStyles.globalPFTextfieldLabel}>
                                Thumbnail<span className={styles.requiredMark}> *</span>
                            </Typography>
                            {thumbnailUploading && <CircularProgress size={18} className={styles.progress} />}
                        </div>
                        {!hasThumbnail && (
                            <GlobalTextfield
                                required={true}
                                fullWidth
                                type="file"
                                inputProps={{ accept: "image/*" }}
                                id="thumbnailUpload"
                                variant="outlined"
                                stylingClassName={`${globalStyles.globalPFTextfield}`}
                                onChange={handleThumbnailUpload}
                            />
                        )}
                        {hasThumbnail ? (
                            <div className={styles.chipWrapper}>
                                <Chip
                                    className={styles.chip}
                                    title={
                                        selectedResorceData?.thumbnail_file_name ||
                                        selectedResorceData?.resource_file_data?.[1]?.thumbnail_name
                                    }
                                    label={
                                        selectedResorceData?.thumbnail_file_name ||
                                        selectedResorceData?.resource_file_data?.[1]?.thumbnail_name
                                    }
                                    onDelete={() => handleRemoveFile("thumbnail")}
                                    deleteIcon={<CancelIcon />}
                                    onClick={() => {
                                        window.open(
                                            `${selectedResorceData?.resource_file_data?.[1]?.thumbnail_url}`
                                        )
                                    }}
                                />
                            </div>
                        ) : null}
                    </div>

                    <div className={styles.fieldBlock}>
                        <div className={styles.labelWithSpinner}>
                            <Typography component="span" className={globalStyles.globalPFTextfieldLabel}>
                                {resourceFileLabel}
                                {!selectedResorceData?.resource_coming_soon && (
                                    <span className={styles.requiredMark}> *</span>
                                )}
                            </Typography>
                            {resourceUploading && <CircularProgress size={18} className={styles.progress} />}
                        </div>
                        {tabValue === "Video" && (
                            <>
                                {!hasResourceFile && (
                                    <GlobalTextfield
                                        required={!selectedResorceData?.resource_coming_soon}
                                        fullWidth
                                        type="file"
                                        inputProps={{ accept: "video/*" }}
                                        id="videoUpload"
                                        variant="outlined"
                                        stylingClassName={`${globalStyles.globalPFTextfield}`}
                                        onChange={handleResourceUpload}
                                    />
                                )}
                                {hasResourceFile ? (
                                    <div className={styles.chipWrapper}>
                                        <Chip
                                            className={styles.chip}
                                            title={
                                                selectedResorceData?.resource_file_name ||
                                                selectedResorceData?.resource_file_data?.[0]?.file_name
                                            }
                                            label={
                                                selectedResorceData?.resource_file_name ||
                                                selectedResorceData?.resource_file_data?.[0]?.file_name
                                            }
                                            onDelete={() => handleRemoveFile("resource")}
                                            deleteIcon={<CancelIcon />}
                                            onClick={() => {
                                                window.open(
                                                    `${selectedResorceData?.resource_file_data?.[0]?.file_url}`
                                                )
                                            }}
                                        />
                                    </div>
                                ) : null}
                            </>
                        )}
                        {tabValue === "Document" && (
                            <>
                                {!hasResourceFile && (
                                    <GlobalTextfield
                                        required={!selectedResorceData?.resource_coming_soon}
                                        fullWidth
                                        type="file"
                                        inputProps={{ accept: "application/pdf" }}
                                        id="documentUpload"
                                        variant="outlined"
                                        stylingClassName={`${globalStyles.globalPFTextfield}`}
                                        onChange={handleResourceUpload}
                                    />
                                )}
                                {hasResourceFile ? (
                                    <div className={styles.chipWrapper}>
                                        <Chip
                                            className={styles.chip}
                                            title={
                                                selectedResorceData?.resource_file_name ||
                                                selectedResorceData?.resource_file_data?.[0]?.file_name
                                            }
                                            label={
                                                selectedResorceData?.resource_file_name ||
                                                selectedResorceData?.resource_file_data?.[0]?.file_name
                                            }
                                            onDelete={() => handleRemoveFile("resource")}
                                            deleteIcon={<CancelIcon />}
                                            onClick={() => {
                                                window.open(
                                                    `${selectedResorceData?.resource_file_data?.[0]?.file_url}`
                                                )
                                            }}
                                        />
                                    </div>
                                ) : null}
                            </>
                        )}
                    </div>
                </div>

                <div className={styles.fieldBlock}>
                    <Typography component="span" className={globalStyles.globalPFTextfieldLabel}>
                        Coming Soon<span className={styles.requiredMark}> *</span>
                    </Typography>
                    <RadioGroup
                        row
                        className={styles.radioRow}
                        aria-labelledby="coming-soon-radio-group"
                        name="coming-soon-radio-group"
                        value={selectedResorceData?.resource_coming_soon ? "true" : "false"}
                        onChange={(e) =>
                            handleInputChange("resource_coming_soon", e.target.value === "true")
                        }
                    >
                        <FormControlLabel
                            value="true"
                            control={<Radio className={styles.radioPrimary} />}
                            label="Yes"
                        />
                        <FormControlLabel
                            value="false"
                            control={<Radio className={styles.radioPrimary} />}
                            label="No"
                        />
                    </RadioGroup>
                </div>

                <div className={styles.footer}>
                    <Button
                        variant="outlined"
                        color="primary"
                        className={styles.buttonCancel}
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        className={styles.buttonSubmit}
                        onClick={onSubmit}
                    >
                        {isEditModeTrainingPage ? "Update" : "Add"}
                    </Button>
                </div>
            </Stack>
        </Grid>
    )
}

export default AddNewResource
